'use client';

import { useMemo, memo } from 'react';
import type { UIMessage } from 'ai';
import { isToolUIPart } from 'ai';
import ReactMarkdown from 'react-markdown';
import { useClientConfig } from '@/lib/clients/ClientContext';

/** Strip [SUGGESTIONS: ...] tag — handles both complete and partial (during streaming) */
export function stripSuggestions(text: string): string {
  return text.replace(/\[SUGGESTIONS:[^\]]*\]?/, '').trim();
}

interface AgentChatMessageProps {
  message: UIMessage;
  suggestions?: string[];
  onChipClick?: (chip: string) => void;
  isLatestAssistant?: boolean;
}

const markdownComponents = {
  p: ({ children }: { children?: React.ReactNode }) => <p className="mb-1.5 last:mb-0">{children}</p>,
  strong: ({ children }: { children?: React.ReactNode }) => <strong className="font-semibold">{children}</strong>,
  ul: ({ children }: { children?: React.ReactNode }) => <ul className="list-disc pl-4 mb-1.5">{children}</ul>,
  ol: ({ children }: { children?: React.ReactNode }) => <ol className="list-decimal pl-4 mb-1.5">{children}</ol>,
  li: ({ children }: { children?: React.ReactNode }) => <li className="mb-0.5">{children}</li>,
};

export default memo(function AgentChatMessage({
  message,
  suggestions,
  onChipClick,
  isLatestAssistant,
}: AgentChatMessageProps) {
  const isUser = message.role === 'user';

  // Link target follows the per-client config (default '_blank'). '_self' keeps
  // navigation in the current window — matches the product-card behaviour.
  const clientConfig = useClientConfig();
  const components = useMemo(() => {
    const linkTarget = clientConfig.ui.productLinkTarget ?? '_blank';
    const linkRel = linkTarget === '_blank' ? 'noopener noreferrer' : undefined;
    return {
      ...markdownComponents,
      a: ({ href, children }: { href?: string; children?: React.ReactNode }) => (
        <a href={href} target={linkTarget} rel={linkRel} className="text-brand underline hover:opacity-80">
          {children}
        </a>
      ),
    };
  }, [clientConfig.ui.productLinkTarget]);

  // Single pass over parts: extract text, streaming state, tool state
  const { rawText, isTextStreaming, hasActiveToolCall } = useMemo(() => {
    let text = '';
    let streaming = false;
    let toolActive = false;

    for (const part of message.parts) {
      if (part.type === 'text') {
        text += part.text;
        if (part.state === 'streaming') streaming = true;
      } else if (isToolUIPart(part)) {
        if (part.state === 'input-streaming' || part.state === 'input-available') {
          toolActive = true;
        }
      }
    }

    return { rawText: text, isTextStreaming: streaming, hasActiveToolCall: toolActive };
  }, [message.parts]);

  // Strip [SUGGESTIONS: ...] for display (handles partial tags during streaming)
  const textContent = useMemo(() => stripSuggestions(rawText), [rawText]);

  return (
    <div className={`flex flex-col ${isUser ? 'items-end' : 'items-start'} gap-2`}>
      {/* Tool call indicator. The ring is border-current, NOT border-brand:
          .border-brand is !important, so it beat border-t-transparent and drew
          a full ring whose spin could not be seen. Colour = text-highlight,
          readable on the pale pill (same reason as the chips below). */}
      {hasActiveToolCall && (
        <div className="flex items-center gap-2 px-3 py-2 bg-brand/10 rounded-lg animate-fadeIn text-highlight">
          <div className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" />
          <span className="text-[12px] font-gotham">
            Αναζήτηση προϊόντων...
          </span>
        </div>
      )}

      {/* Message bubble */}
      {(textContent || isUser) && (
        <div
          className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-[13px] font-gotham leading-[20px] ${
            isUser
              ? 'bg-brand text-brand-text rounded-br-md'
              : 'bg-[#F3F4F6] text-text-primary rounded-bl-md'
          }`}
        >
          {isUser ? (
            <span className="whitespace-pre-wrap break-words">{rawText}</span>
          ) : isTextStreaming ? (
            // During streaming: plain text — no markdown parsing overhead
            <span className="whitespace-pre-wrap break-words">
              {textContent}
              <span className="inline-block w-0.5 h-4 bg-brand ml-0.5 animate-pulse align-text-bottom" />
            </span>
          ) : (
            // Streaming done: render markdown once
            <div className="agent-chat-markdown break-words">
              <ReactMarkdown components={components}>
                {textContent}
              </ReactMarkdown>
            </div>
          )}
          {/* The "no words yet" dots that used to sit here could never render
              (this bubble needs text to exist). They live in
              AgentChatSidebar now, which also sees the wait BEFORE the reply
              message exists. */}
        </div>
      )}

      {/* Suggestion chips — only on latest assistant message, after streaming completes.
          The label is brand-coloured TEXT on a near-white pill, so it uses
          text-highlight (ui.highlightColor, falling back to the brand colour):
          kostaboda's turquoise is 1.86:1 as text and the chips were barely
          legible (founder, 2026-11-03). Clients without highlightColor are unchanged. */}
      {isLatestAssistant && !isTextStreaming && suggestions && suggestions.length > 0 && onChipClick && (
        <div className="flex flex-wrap gap-1.5 max-w-[85%]">
          {suggestions.map(chip => (
            <button
              key={chip}
              onClick={() => onChipClick(chip)}
              className="px-3 py-1.5 rounded-full bg-brand/10 hover:bg-brand/20 transition-colors text-[11px] font-gotham font-medium text-highlight cursor-pointer"
            >
              {chip}
            </button>
          ))}
        </div>
      )}
    </div>
  );
});
